/**
 * 绑定/解绑微信弹窗
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle, Loader2, Check, AlertCircle, RefreshCw, Unlink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import securityService from '../../services/securityService';

const BindWechatModal = ({ isOpen, onClose, isBound, wechatNickname, onSuccess }) => {
    const { token } = useAuth();

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [session, setSession] = useState(null);
    const [status, setStatus] = useState('idle'); // idle, pending, bound, expired
    const [pollingTimer, setPollingTimer] = useState(null);

    // 清理定时器
    const clearPolling = useCallback(() => {
        if (pollingTimer) {
            clearInterval(pollingTimer);
            setPollingTimer(null);
        }
    }, [pollingTimer]);

    // 重置状态
    const resetState = useCallback(() => {
        setSession(null);
        setStatus('idle');
        setError('');
        clearPolling();
    }, [clearPolling]);

    useEffect(() => {
        if (!isOpen) {
            resetState();
        }
    }, [isOpen, resetState]);

    // 卸载时清理
    useEffect(() => {
        return () => clearPolling();
    }, [clearPolling]);

    // 创建绑定会话
    const createBindSession = async () => {
        setLoading(true);
        setError('');
        try {
            const data = await securityService.createWechatBindSession(token);
            setSession(data);
            setStatus('pending');
            startPolling(data.ticket);
        } catch (err) {
            setError(err.response?.data?.error || '创建会话失败');
        } finally {
            setLoading(false);
        }
    };

    // 轮询检查状态
    function startPolling(ticket) {
        const timer = setInterval(async () => {
            try {
                const data = await securityService.checkWechatBindStatus(token, ticket);
                if (data.status === 'bound') {
                    clearPolling();
                    setStatus('bound');
                    setTimeout(() => {
                        onSuccess?.();
                        onClose();
                    }, 1500);
                } else if (data.status === 'expired') {
                    clearPolling();
                    setStatus('expired');
                    setError('二维码已过期，请重新扫码');
                }
            } catch (err) {
                if (err.response?.data?.status === 'expired') {
                    clearPolling();
                    setStatus('expired');
                    setError(err.response?.data?.error || '二维码已过期或处理失败');
                }
            }
        }, 2000);
        setPollingTimer(timer);
    }

    const handleMockBind = async () => {
        if (!session?.ticket) return;
        setLoading(true);
        setError('');
        try {
            await securityService.mockBindWechat(token, session.ticket);
        } catch (err) {
            setError(err.response?.data?.error || '模拟绑定失败');
        } finally {
            setLoading(false);
        }
    };

    // 解绑微信
    const handleUnbind = async () => {
        if (!window.confirm('确定要解绑微信吗？解绑后将无法使用微信登录。')) {
            return;
        }

        setLoading(true);
        setError('');
        try {
            await securityService.unbindWechat(token);
            onSuccess?.();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || '解绑失败');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
                    />
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.95, y: 20 }}
                        className="fixed inset-0 flex items-center justify-center z-50 p-4"
                    >
                        <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
                            {/* 头部 */}
                            <div className="relative px-6 pt-6 pb-4 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                                        <MessageCircle className="w-5 h-5 text-green-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-lg font-bold text-gray-900">
                                            {isBound ? '解绑微信' : '绑定微信'}
                                        </h2>
                                        <p className="text-xs text-gray-500">
                                            {isBound ? `已绑定：${wechatNickname}` : '扫码绑定微信账号'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={onClose}
                                    className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"
                                >
                                    <X className="w-4 h-4 text-gray-500" />
                                </button>
                            </div>

                            <div className="p-6">
                                {/* 错误提示 */}
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600"
                                    >
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        <span>{error}</span>
                                    </motion.div>
                                )}

                                {isBound ? (
                                    /* 已绑定状态 - 显示解绑选项 */
                                    <div className="text-center py-4">
                                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                                            <MessageCircle className="w-10 h-10 text-green-600" />
                                        </div>
                                        <p className="text-gray-600 mb-6">
                                            已绑定微信账号：<span className="font-bold text-gray-900">{wechatNickname}</span>
                                        </p>
                                        <button
                                            onClick={handleUnbind}
                                            disabled={loading}
                                            className="w-full py-3 rounded-xl font-bold text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 flex items-center justify-center gap-2"
                                        >
                                            {loading ? (
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                            ) : (
                                                <Unlink className="w-5 h-5" />
                                            )}
                                            <span>解绑微信</span>
                                        </button>
                                    </div>
                                ) : status === 'idle' ? (
                                    /* 未绑定 - 开始绑定 */
                                    <div className="text-center py-4">
                                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                                            <MessageCircle className="w-10 h-10 text-gray-400" />
                                        </div>
                                        <p className="text-gray-500 mb-6">
                                            绑定微信后可使用微信扫码登录
                                        </p>
                                        <button
                                            onClick={createBindSession}
                                            disabled={loading}
                                            className="w-full py-3 rounded-xl font-bold text-white bg-gradient-to-r from-green-500 to-green-600 hover:shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
                                        >
                                            {loading ? (
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                            ) : (
                                                <MessageCircle className="w-5 h-5" />
                                            )}
                                            <span>开始绑定</span>
                                        </button>
                                    </div>
                                ) : status === 'pending' ? (
                                    /* 等待扫码 */
                                    <div className="text-center py-4">
                                        <div className="w-48 h-48 mx-auto mb-4 bg-gray-100 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300">
                                            {session?.qr_url ? (
                                                <img src={session.qr_url} alt="微信二维码" className="w-full h-full object-contain" />
                                            ) : (
                                                <div className="text-center text-gray-400">
                                                    <MessageCircle className="w-12 h-12 mx-auto mb-2" />
                                                    <p className="text-xs">请在微信中扫码</p>
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex items-center justify-center gap-2 text-gray-500">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            <span className="text-sm">等待扫码确认...</span>
                                        </div>
                                        <button
                                            onClick={() => {
                                                clearPolling();
                                                createBindSession();
                                            }}
                                            className="mt-4 text-sm text-blue-500 hover:underline flex items-center justify-center gap-1"
                                        >
                                            <RefreshCw className="w-3 h-3" />
                                            刷新二维码
                                        </button>
                                        {import.meta.env?.DEV && (
                                            <button
                                                onClick={handleMockBind}
                                                className="mt-2 text-[11px] px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200"
                                            >
                                                模拟微信绑定（开发）
                                            </button>
                                        )}
                                    </div>
                                ) : status === 'bound' ? (
                                    /* 绑定成功 */
                                    <div className="text-center py-8">
                                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                                            <Check className="w-8 h-8 text-green-600" />
                                        </div>
                                        <p className="text-lg font-bold text-green-600">绑定成功！</p>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default BindWechatModal;
